import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"

import {cn} from "@/lib/utils"

const Progress = React.forwardRef<
    React.ElementRef<typeof ProgressPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>
>(({className, value, ...props}, ref) => (
    <ProgressPrimitive.Root
        ref={ref}
        className={cn(
            "relative h-2 w-full overflow-hidden rounded-full bg-primary/20",
            className
        )}
        {...props}
    >
        <ProgressPrimitive.Indicator
            className="h-full w-full flex-1 bg-primary transition-all"
            style={{transform: `translateX(-${100 - (value || 0)}%)`}}
        />
    </ProgressPrimitive.Root>
))
Progress.displayName = ProgressPrimitive.Root.displayName

export type SegmentedProgressTone = 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'neutral'

export interface ProgressSegment {
    percent: number
    tone: SegmentedProgressTone
}

interface SegmentedProgressProps {
    segments: ProgressSegment[]
    className?: string
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const toneBgClass: Record<SegmentedProgressTone, string> = {
    success: 'bg-status-success',
    warning: 'bg-status-warning',
    danger: 'bg-status-danger',
    info: 'bg-status-info',
    brand: 'bg-primary',
    neutral: 'bg-muted-foreground/30',
}
/* eslint-enable i18next/no-literal-string */

/**
 * Horizontal track rendering multiple absolute-positioned segments.
 * Used for stacked-bar charts where one bar summarises multiple
 * counts (e.g. kept vs. revoked vs. pending).
 */
function SegmentedProgress({segments, className, 'data-testid': testId}: SegmentedProgressProps) {
    let cursor = 0
    return (
        <div
            className={cn('relative h-3 w-full overflow-hidden rounded-full bg-muted', className)}
            data-testid={testId}
        >
            {segments.map((seg, idx) => {
                const style = {left: `${cursor}%`, width: `${seg.percent}%`}
                cursor += seg.percent
                return (
                    <div
                        key={idx}
                        className={cn('absolute inset-y-0 transition-all', toneBgClass[seg.tone])}
                        style={style}
                    />
                )
            })}
        </div>
    )
}

export {Progress, SegmentedProgress}
